import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

// Phase 4a admin-sweep — "CTC" is an India concept; a UK org's admin sees
// "Annual Salary" and £ instead. Region reliably implies the currency here
// (it's the org's payroll region), so this is not fabricated currency logic.
function fmtFor(region) {
  const sym = region === "India" ? "₹" : region === "UK" ? "£" : "";
  const loc = region === "India" ? "en-IN" : "en-GB";
  return (n) => (n != null ? `${sym}${Number(n).toLocaleString(loc)}` : "—");
}

export default function EmployeePayDrawer({ open, row, onClose, onSaveAnnualSalary, onAddAdjustment, region = "India" }) {
  const [ctc,     setCTC]     = useState(0);
  const [note,    setNote]    = useState("");
  const [eff,     setEff]     = useState(new Date().toISOString().slice(0, 10));
  const [adjType, setAdjType] = useState("EARNING");
  const [adjAmt,  setAdjAmt]  = useState("");
  const [adjLabel,setAdjLabel]= useState("");
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (row) { setCTC(row.currentAnnualSalary || 0); setNote(""); }
  }, [row?.id]);

  if (!open || !row) return null;

  const isIndia    = region === "India";
  const fmt        = fmtFor(region);
  const salaryTerm = isIndia ? "CTC" : "Annual Salary";   // "CTC" is India-only

  const saveCTC = async () => {
    setSaving(true);
    try { await onSaveAnnualSalary?.(row.id, { ctc, note, effectiveDate: eff }); }
    finally { setSaving(false); }
  };

  const addAdj = async () => {
    if (!adjAmt) return;
    await onAddAdjustment?.(row.id, {
      type: adjType,
      code: adjType === "EARNING" ? "ADHOC_PAY" : "ADHOC_DED",
      label: adjLabel || "Ad-hoc",
      amount: Number(adjAmt),
      oneTime: true,
    });
    setAdjAmt(""); setAdjLabel("");
  };

  return (
    <div className="fixed inset-0 z-[10070]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[500px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="font-semibold text-white">{row.name}</p>
            <p className="text-xs text-[#00ff90]/50 mt-0.5">{row.empCode} · {row.dept}</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Current pay info */}
          <div className="rounded-xl border border-white/5 bg-white/3 divide-y divide-white/5">
            {[
              ["Location",                    row.location],
              [`Current Annual ${salaryTerm}`, fmt(row.currentAnnualSalary)],
              ["Monthly Gross",               fmt(row.earnings)],
              ["Monthly Net",       fmt(row.net)],
              ["Last Appraisal",    row.appraisal?.last || "—"],
              ["Next Appraisal",    row.appraisal?.next || "—"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-xs text-[#00ff90]/50 uppercase tracking-wider">{k}</span>
                <span className="text-sm text-white/70">{v}</span>
              </div>
            ))}
          </div>

          {/* Update salary / CTC */}
          <div>
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-3">Update Annual {salaryTerm}</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>New Annual {salaryTerm}</label>
                  <input type="number" className={inputCls} value={ctc}
                    onChange={e => setCTC(Number(e.target.value || 0))} />
                </div>
                <div>
                  <label className={labelCls}>Effective Date</label>
                  <input type="date" className={inputCls} value={eff}
                    onChange={e => setEff(e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Note / Reason</label>
                <input className={inputCls} value={note} onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Annual increment 2025" />
              </div>
              <button onClick={saveCTC} disabled={saving}
                className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
                {saving ? "Saving…" : `Save ${salaryTerm}`}
              </button>
            </div>
          </div>

          {/* One-time adjustment */}
          <div>
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-3">One-time Adjustment (This Run)</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Type</label>
                  <select className={inputCls} value={adjType} onChange={e => setAdjType(e.target.value)}>
                    <option value="EARNING">Earning (+)</option>
                    <option value="DEDUCTION">Deduction (−)</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Amount{isIndia ? " (₹)" : region === "UK" ? " (£)" : ""}</label>
                  <input type="number" className={inputCls} value={adjAmt}
                    onChange={e => setAdjAmt(e.target.value)} placeholder="0" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Label</label>
                <input className={inputCls} value={adjLabel} onChange={e => setAdjLabel(e.target.value)}
                  placeholder="e.g. Performance bonus" />
              </div>
              <button onClick={addAdj} disabled={!adjAmt}
                className="text-xs px-5 py-2 rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25 disabled:opacity-40 transition-colors font-medium">
                Add Adjustment
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}